import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GIFT_MAX_CENTS,
  berlinDate,
  GIFT_SALE_METHOD_LABELS,
  GIFT_SALE_METHODS,
  GIFT_STATUS_LABELS,
  GIFT_STATUSES,
  formatCents,
  formatGiftCode,
  isValidGiftCode,
  type GiftCheck,
  type GiftIssued,
  type GiftLiabilities,
  type GiftSaleMethod,
  type GiftSaleOption,
  type GiftStatus,
  type GiftVoucher,
  type GiftVoucherDetail,
} from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { InfoHint } from '../../components/InfoHint';
import { centsText, parseCents } from '../../money';
import { useSession } from '../../session';
import './vouchers.css';

export const dayDe = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '–');
export const giftBadge = (s: GiftStatus) => <span className={`badge ${s === 'active' ? 'badge--won' : s === 'void' ? 'badge--rejected' : ''}`}>{GIFT_STATUS_LABELS[s]}</span>;
const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : e instanceof Error ? e.message : 'Das hat nicht geklappt.');

/** PDF zum Gutschein laden. Der Code kommt mit, denn Wirkia speichert ihn nicht. */
export async function downloadGiftPdf(id: string, code: string, number: string): Promise<void> {
  const res = await fetch(`/api/v1/gift-vouchers/${id}/pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Kontor-Client': 'backoffice' }, credentials: 'same-origin', body: JSON.stringify({ code }) });
  if (!res.ok) throw new Error('Das PDF konnte nicht erstellt werden.');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `Gutschein-${number}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Gutscheine: ein Gutschein ist ein persönliches Guthaben mit eigenem Code, das der Betrieb ausstellt und das sich in Teilbeträgen einlösen lässt. Oben prüft und löst das Büro einen Code ein,
 * darunter stellt es neue Gutscheine aus, darunter steht die Liste. Wirkia speichert nur den Hash des Codes: Der Code erscheint beim Ausstellen genau einmal.
 */
export function GiftVouchersPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const write = can('vouchers.write');
  const [params, setParams] = useSearchParams();
  const status = params.get('stand') ?? '';
  const q = params.get('q') ?? '';
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<GiftIssued | null>(null);

  const list = useQuery({ queryKey: ['gift-vouchers', { status, q }], queryFn: async () => (await api<{ data: GiftVoucher[] }>('GET', `/gift-vouchers${queryString({ status, q })}`)).data });
  const refresh = () => void client.invalidateQueries({ queryKey: ['gift-vouchers'] });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };
  const fields: FilterField[] = [{ key: 'stand', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...GIFT_STATUSES.map((s) => ({ value: s, label: GIFT_STATUS_LABELS[s] }))] }];

  return (
    <>
      <div className="page-head">
        <h1>Gutscheine</h1>
        <span className="muted">{list.data ? `${list.data.length} Gutscheine` : ''}</span>
        {write && <button className="btn btn--primary btn--sm page-head__action" type="button" onClick={() => { setIssuing((v) => !v); setIssued(null); }}>{issuing ? 'Abbrechen' : 'Neuer Gutschein'}</button>}
        <Link className="btn btn--secondary btn--sm" to="/rabattcodes">Rabattcodes</Link>
      </div>
      <p className="muted">Ein Gutschein ist ein persönliches Guthaben mit eigenem Code, das Sie ausstellen und der Inhaber ganz oder in Teilbeträgen einlöst. Feste Codes für Aktionen, die jeder nutzen kann, finden Sie unter „Rabattcodes“.</p>

      {issued && <IssuedCard issued={issued} onClose={() => setIssued(null)} />}
      {write && issuing && !issued && <IssueForm onIssued={(r) => { setIssued(r); setIssuing(false); refresh(); }} />}
      <RedeemCard write={write} onChanged={refresh} />
      <LiabilitiesCard />

      <FilterBar search={{ value: q, placeholder: 'Nummer, Empfänger, Käufer oder Ende des Codes', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ stand: status }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Nummer</th><th>Empfänger</th><th>Käufer</th><th className="num">Betrag</th><th className="num">Guthaben</th><th>Gültig bis</th><th>Stand</th></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data && list.data.length === 0 && <tr><td colSpan={7} className="empty">{q || status ? 'Keine Gutscheine zu dieser Suche.' : 'Noch keine Gutscheine. Stellen Sie den ersten über „Neuer Gutschein“ aus.'}</td></tr>}
            {list.data?.map((v) => (
              <ExpandRow key={v.id} summary={<Summary title={<Link to={`/gutscheine/${v.id}`} className="strong-link">{v.number}</Link>} line2={`${v.recipientName ?? v.buyerName ?? 'ohne Empfänger'} · ${GIFT_STATUS_LABELS[v.status]}`} line3={`Guthaben ${formatCents(v.balanceCents)} von ${formatCents(v.initialCents)}`} />}>
                <td data-label="Nummer" className="nowrap"><Link to={`/gutscheine/${v.id}`} className="strong-link">{v.number}</Link> <span className="muted small">…{v.codeHint}</span></td>
                <td data-label="Empfänger">{v.recipientName ?? '–'}</td>
                <td data-label="Käufer">{v.buyerName ?? '–'}</td>
                <td data-label="Betrag" className="num">{formatCents(v.initialCents)}</td>
                <td data-label="Guthaben" className="num"><strong>{formatCents(v.balanceCents)}</strong></td>
                <td data-label="Gültig bis">{dayDe(v.expiresOn)}</td>
                <td data-label="Stand">{giftBadge(v.status)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Code prüfen und einlösen */
function RedeemCard({ write, onChanged }: { write: boolean; onChanged: () => void }) {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<GiftCheck | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [done, setDone] = useState<GiftVoucherDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = useMutation({
    mutationFn: async () => (await api<{ data: GiftCheck }>('POST', '/gift-vouchers/check', { code })).data,
    onSuccess: (r) => { setResult(r); setError(null); setDone(null); setAmount(r.voucher && r.valid ? centsText(r.voucher.balanceCents) : ''); },
    onError: (e) => setError(errorText(e)),
  });
  const redeem = useMutation({
    mutationFn: async () => {
      const cents = parseCents(amount);
      if (cents === null || cents <= 0) throw new Error('Bitte einen Betrag angeben.');
      return (await api<{ data: GiftVoucherDetail }>('POST', '/gift-vouchers/redeem', { code, amountCents: cents, reference: reference.trim() || null })).data;
    },
    onSuccess: (d) => { setDone(d); setResult(null); setCode(''); setAmount(''); setReference(''); setError(null); onChanged(); },
    onError: (e) => setError(errorText(e)),
  });
  const typo = code.replace(/[^A-Za-z0-9]/g, '').length >= 16 && !isValidGiftCode(code);

  return (
    <section className="card" aria-labelledby="gift-redeem">
      <h2 id="gift-redeem">Gutschein prüfen und einlösen</h2>
      <form className="gift-redeem" onSubmit={(e: FormEvent) => { e.preventDefault(); setError(null); check.mutate(); }} noValidate>
        <label className="field">
          <span>Code des Gutscheins</span>
          <input className="input gift-code-input" value={code} onChange={(e) => { setCode(e.target.value); setResult(null); setDone(null); }} placeholder="XXXX-XXXX-XXXX-XXXX" autoCapitalize="characters" autoComplete="off" spellCheck={false} aria-invalid={typo} />
          {typo && <small className="field__error" role="alert">Der Code stimmt nicht: Eine Stelle ist falsch geschrieben. Es gibt keine 0, O, 1, I, L, Q und U in den Codes.</small>}
        </label>
        <button className="btn btn--secondary" type="submit" disabled={check.isPending || code.trim().length < 8}>{check.isPending ? 'Wird geprüft …' : 'Prüfen'}</button>
      </form>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {done && <div className="alert alert--success" role="status">Eingelöst. Auf dem Gutschein {done.number} bleiben {formatCents(done.balanceCents)}. <Link to={`/gutscheine/${done.id}`}>Zum Gutschein</Link></div>}
      {result && !result.valid && <div className="alert alert--warn" role="status">{result.reason}{result.voucher ? ` (Gutschein ${result.voucher.number})` : ''}</div>}
      {result?.valid && result.voucher && (
        <div className="gift-found">
          <dl className="facts">
            <dt>Gutschein</dt><dd><Link to={`/gutscheine/${result.voucher.id}`}>{result.voucher.number}</Link></dd>
            <dt>Guthaben</dt><dd><strong>{formatCents(result.voucher.balanceCents)}</strong> von {formatCents(result.voucher.initialCents)}</dd>
            <dt>Gültig bis</dt><dd>{dayDe(result.voucher.expiresOn)}</dd>
            {result.voucher.recipientName && (<><dt>Empfänger</dt><dd>{result.voucher.recipientName}</dd></>)}
          </dl>
          {write ? (
            <form className="form-grid form-grid--aligned" onSubmit={(e: FormEvent) => { e.preventDefault(); setError(null); redeem.mutate(); }} noValidate>
              <label className="field"><span>Betrag einlösen €</span><input className="input num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
              <label className="field"><span>Bezug (optional)</span><input className="input" value={reference} maxLength={200} onChange={(e) => setReference(e.target.value)} placeholder="Rechnungs- oder Auftragsnummer" /></label>
              <div className="form-actions"><button className="btn btn--primary" type="submit" disabled={redeem.isPending}>{redeem.isPending ? 'Wird eingelöst …' : 'Einlösen'}</button></div>
            </form>
          ) : <p className="muted small">Zum Einlösen fehlt Ihnen das Recht „Gutscheine: Ändern“.</p>}
        </div>
      )}
    </section>
  );
}

/** Gutschein ausstellen */
function IssueForm({ onIssued }: { onIssued: (r: GiftIssued) => void }) {
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [buyer, setBuyer] = useState<PickedContact | null>(null);
  const [buyerName, setBuyerName] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [message, setMessage] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sold, setSold] = useState(true);
  const [productId, setProductId] = useState('');
  const [method, setMethod] = useState<GiftSaleMethod>('bank_transfer');
  const [paidOn, setPaidOn] = useState(berlinDate());
  const options = useQuery({ queryKey: ['gift-sale-options'], queryFn: async () => (await api<{ data: GiftSaleOption[] }>('GET', '/gift-vouchers/sale-options')).data });
  const chosen = options.data?.find((o) => o.productId === (productId || options.data?.[0]?.productId));

  const create = useMutation({
    mutationFn: async () => {
      const cents = parseCents(amount);
      if (cents === null) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { amountCents: 'Bitte einen Betrag angeben.' });
      return (await api<{ data: GiftIssued }>('POST', '/gift-vouchers', { amountCents: cents, recipientName: recipient.trim() || null, buyerContactId: buyer?.id ?? null, buyerName: buyer ? null : buyerName.trim() || null, expiresOn: expiresOn || null, message: message.trim() || null, note: note.trim() || null, sale: sold ? { productId: chosen?.productId ?? '', method, paidOn } : null })).data;
    },
    onSuccess: onIssued,
    onError: (e) => setErrors(e instanceof ApiError && e.status === 422 ? e.fields : { _: errorText(e) }),
  });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); setErrors({}); create.mutate(); }} noValidate>
      <h2>Neuer Gutschein</h2>
      {errors._ && <div className="alert alert--error" role="alert">{errors._}</div>}
      <fieldset className="gift-sale">
        <legend>Wie entsteht der Gutschein?</legend>
        <label className="check"><input type="radio" name="gift-origin" checked={sold} onChange={() => setSold(true)} /> <span>Verkauft: Der Käufer hat bezahlt</span></label>
        <label className="check"><input type="radio" name="gift-origin" checked={!sold} onChange={() => setSold(false)} /> <span>Verschenkt: Es gibt keinen Zahlungseingang</span></label>
        {sold && (
          <div className="form-grid gift-issue-grid">
            <label className="field">
              <span className="invoice-label">Gutschein-Artikel<InfoHint label="Gutschein-Artikel">Ein Produkt mit einer Buchungsgruppe der Art „Gutschein“. Die Gruppe nennt das Sachkonto der Verbindlichkeit, das Ihr Steuerberater festgelegt hat. Der Verkauf eines Mehrzweck-Gutscheins ist noch kein Umsatz, der Erlös entsteht bei der Einlösung.</InfoHint></span>
              <select className="input" value={chosen?.productId ?? ''} onChange={(e) => setProductId(e.target.value)} aria-invalid={!!errors.sale} disabled={!options.data?.length}>
                {!options.data?.length && <option value="">Kein Gutschein-Artikel vorhanden</option>}
                {options.data?.map((o) => <option key={o.productId} value={o.productId}>{o.name} · Konto {o.accountNumber ?? 'fehlt'}</option>)}
              </select>
              {errors.sale && <small className="field__error" role="alert">{errors.sale}</small>}
              {options.data && options.data.length === 0 && <small className="muted">Legen Sie in der <Link to="/einrichtung/buchungsgruppen">Einrichtung</Link> eine Buchungsgruppe der Art „Gutschein“ mit Sachkonto an und weisen Sie sie einem Produkt zu.</small>}
              {chosen && <small className="muted">Verbindlichkeit auf Konto {chosen.accountNumber ?? '–'}{chosen.accountName ? ` · ${chosen.accountName}` : ''}</small>}
            </label>
            <label className="field">
              <span>Bezahlt per</span>
              <select className="input" value={method} onChange={(e) => setMethod(e.target.value as GiftSaleMethod)}>{GIFT_SALE_METHODS.map((m) => <option key={m} value={m}>{GIFT_SALE_METHOD_LABELS[m]}</option>)}</select>
            </label>
            <label className="field"><span>Bezahlt am</span><input className="input" type="date" value={paidOn} max={berlinDate()} onChange={(e) => setPaidOn(e.target.value)} /></label>
          </div>
        )}
        {!sold && <small className="muted">Ein verschenkter Gutschein wird nicht gebucht. Beim Einlösen mindert er die Rechnung, der Betrag ist dann Rabatt oder Werbeaufwand.</small>}
      </fieldset>
      <div className="gift-amounts" role="group" aria-label="Betrag wählen">
        {[25, 50, 100, 200].map((n) => <button key={n} type="button" className={`btn btn--secondary btn--sm${amount === centsText(n * 100) ? ' is-active' : ''}`} onClick={() => setAmount(centsText(n * 100))}>{n} €</button>)}
      </div>
      <div className="form-grid gift-issue-grid">
        <label className="field"><span className="invoice-label">Betrag € <abbr title="Pflichtfeld">*</abbr><InfoHint label="Bruttobetrag">Der Wert des Gutscheins ist ein Bruttobetrag: der Betrag, den der Käufer zahlt und der später auf Rechnungen einlösbar ist.</InfoHint></span><input className="input num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" aria-invalid={!!errors.amountCents} />{err('amountCents')}<small className="muted gift-hint">Höchstens {formatCents(GIFT_MAX_CENTS)}</small></label>
        <label className="field"><span>Für (Empfänger)</span><input className="input" value={recipient} maxLength={120} onChange={(e) => setRecipient(e.target.value)} placeholder="Name auf dem Gutschein" /></label>
        <label className="field">
          <span className="invoice-label">Gültig bis<InfoHint label="Gültigkeit">Ohne Angabe gilt der Gutschein drei Jahre, bis zum Ende des dritten Jahres nach der Ausstellung (gesetzliche Verjährung nach § 195 BGB). Sie können ihn später verlängern.</InfoHint></span>
          <input className="input" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} aria-invalid={!!errors.expiresOn} />
          {err('expiresOn')}
        </label>
        <div className="field">
          <span>Käufer</span>
          <ContactPicker label="Käufer" value={buyer} onChange={setBuyer} />
          {!buyer && <input className="input" value={buyerName} maxLength={120} onChange={(e) => setBuyerName(e.target.value)} placeholder="oder Name des Käufers" aria-label="Name des Käufers" />}
        </div>
        <label className="field field--wide"><span>Widmung auf dem Gutschein</span><input className="input" value={message} maxLength={300} onChange={(e) => setMessage(e.target.value)} placeholder="Zum Beispiel: Alles Gute zum Geburtstag" /></label>
        <label className="field field--wide"><span>Notiz (nur intern)</span><input className="input" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      <div className="form-actions"><button className="btn btn--primary" type="submit" disabled={create.isPending || !amount.trim() || (sold && !chosen)}>{create.isPending ? 'Wird ausgestellt …' : 'Gutschein ausstellen'}</button></div>
    </form>
  );
}

/** Der Code erscheint nach dem Ausstellen genau einmal */
function IssuedCard({ issued, onClose }: { issued: GiftIssued; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = issued.voucher;
  return (
    <section className="card gift-issued" role="status" aria-labelledby="gift-issued">
      <h2 id="gift-issued">Gutschein {v.number} über {formatCents(v.initialCents)} ausgestellt</h2>
      <p className="gift-issued__code" aria-label="Gutscheincode">{issued.code}</p>
      <div className="alert alert--warn">Notieren oder drucken Sie den Code jetzt. Wirkia speichert ihn nicht, nur seinen Hash, und zeigt ihn nicht noch einmal an. Geht er verloren, sperren Sie den Gutschein und stellen einen neuen aus.</div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="payroll-actions">
        <button className="btn btn--primary" type="button" onClick={() => { downloadGiftPdf(v.id, issued.code, v.number).catch((e) => setError(errorText(e))); }}>Gutschein als PDF</button>
        <button className="btn btn--secondary" type="button" onClick={() => { void navigator.clipboard?.writeText(issued.code).then(() => setCopied(true)); }}>{copied ? 'Kopiert' : 'Code kopieren'}</button>
        <Link className="btn btn--secondary" to={`/gutscheine/${v.id}`}>Zum Gutschein</Link>
        <button className="btn btn--ghost" type="button" onClick={onClose}>Schließen</button>
      </div>
      <p className="muted small">Gültig bis {dayDe(v.expiresOn)}{v.recipientName ? ` · für ${v.recipientName}` : ''}. Code zum Prüfen: {formatGiftCode(issued.code)}</p>
    </section>
  );
}

const euro = (cents: number) => (cents === 0 ? '–' : formatCents(cents));

/**
 * Verbindlichkeiten aus Gutscheinen je Sachkonto, für den Steuerberater: was im Zeitraum verkauft, eingelöst und gesperrt wurde und was heute noch offen ist. Als CSV ladbar.
 * Wirkia bucht nicht, es liefert die Zahlen, die der Berater für die Buchung braucht.
 */
function LiabilitiesCard() {
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(berlinDate());
  const query = useQuery({ queryKey: ['gift-liabilities', from, to], queryFn: async () => (await api<{ data: GiftLiabilities }>('GET', `/gift-vouchers/liabilities${queryString({ from, to })}`)).data, enabled: !!from && !!to && from <= to });
  const data = query.data;
  return (
    <CollapsibleCard title="Verbindlichkeiten für die Buchhaltung" storageKey="gift-liabilities" className="gift-liabilities">
      <p className="muted small">Ein verkaufter Gutschein ist noch kein Umsatz, sondern eine Verbindlichkeit gegenüber dem Inhaber. Hier steht sie je Sachkonto: wie viel verkauft, eingelöst und gesperrt wurde und was heute noch offen ist. Den Erlös zeigt die Rechnung, auf der der Gutschein eingelöst wird.</p>
      <div className="form-grid form-grid--aligned">
        <label className="field"><span>Von</span><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="field"><span>Bis</span><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <div className="form-actions"><a className="btn btn--secondary" href={`/api/v1/gift-vouchers/liabilities${queryString({ from, to, format: 'csv' })}`}>Als CSV laden</a></div>
      </div>
      {query.isLoading && <p className="muted">Wird berechnet …</p>}
      {data && data.accounts.length === 0 && <p className="muted">Im Zeitraum und heute gibt es keine Gutscheinbewegungen.</p>}
      {data && data.accounts.length > 0 && (
        <div className="table-scroll">
          <table className="table gift-liabilities__table">
            <thead><tr><th>Konto</th><th className="num">Verkauft</th><th className="num">Auf Rechnungen</th><th className="num">Eingelöst</th><th className="num">Gesperrt</th><th className="num">Bestand heute</th><th className="num">Abgelaufen</th></tr></thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.accountNumber ?? 'free'}>
                  <td>{a.accountNumber ? <><strong>{a.accountNumber}</strong>{a.accountName && <span className="muted small"> · {a.accountName}</span>}</> : <span>Verschenkt, ohne Konto</span>}</td>
                  <td className="num">{euro(a.soldCents)}</td>
                  <td className="num">{euro(a.invoicedCents)}</td>
                  <td className="num">{euro(a.redeemedCents)}</td>
                  <td className="num">{euro(a.voidedCents)}</td>
                  <td className="num"><strong>{euro(a.openCents)}</strong></td>
                  <td className="num">{euro(a.expiredCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.notes.map((n) => <p key={n} className="muted small">{n}</p>)}
    </CollapsibleCard>
  );
}
